import { createSlice, createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { getRequest, postRequest } from "../config";

export const sendClarenceMessage = createAsyncThunk(
    "clarence/sendMessage",
    async (data, { rejectWithValue }) => {
        try {
            const { companyId, message, conversationId, selectedFiles } = data;
            const response = await postRequest('/clarence/chat', { companyId, message, conversationId, selectedFiles });
            if (response['code'] === 200) return response['body'];
            return rejectWithValue(response['message'] || 'Failed to send message');
        } catch (error) {
            return rejectWithValue(error?.['message'] || 'Something went wrong');
        }
    }
);

export const fetchClarenceConversations = createAsyncThunk(
    "clarence/fetchConversations",
    async (companyId, { rejectWithValue }) => {
        try {
            const response = await getRequest(`/clarence/conversations/${companyId}`);
            if (response['code'] === 200) return response['body']['conversations'];
            return rejectWithValue(response['message'] || 'Failed to fetch conversations');
        } catch (error) {
            return rejectWithValue(error?.['message'] || 'Something went wrong');
        }
    }
);

export const fetchClarenceSuggestions = createAsyncThunk(
    "clarence/fetchSuggestions",
    async (companyId, { rejectWithValue }) => {
        try {
            const response = await getRequest(`/clarence/suggestions/${companyId}`);
            if (response['code'] === 200) return response['body']['suggestions'];
            return rejectWithValue(response['message'] || 'Failed to fetch suggestions');
        } catch (error) {
            return rejectWithValue(error?.['message'] || 'Something went wrong');
        }
    }
);

export const confirmClarenceClassification = createAsyncThunk(
    "clarence/confirmClassification",
    async (data, { rejectWithValue }) => {
        try {
            const { conversationId, classificationId, confirmed, correctedType, correctedCode } = data;
            const response = await postRequest('/clarence/classify/confirm', { conversationId, classificationId, confirmed, correctedType, correctedCode });
            if (response['code'] === 200) return response['body'];
            return rejectWithValue(response['message'] || 'Failed to confirm classification');
        } catch (error) {
            return rejectWithValue(error?.['message'] || 'Something went wrong');
        }
    }
);

const initialState = {
    isOpen: false,
    messages: [],
    conversations: [],
    currentConversationId: null,
    suggestions: [],
    pendingClassification: null,
    agentActions: [],
    isLoading: false,
    isTyping: false,
    error: null
};

export default function getClarenceReducer() {
    const clarenceSlice = createSlice({
        name: "clarence",
        initialState,
        reducers: {
            toggleClarence: (state, action) => {
                state['isOpen'] = action['payload'] !== undefined ? action['payload'] : !state['isOpen'];
            },
            addMessage: (state, action) => {
                state['messages'].push(action['payload']);
            },
            setMessages: (state, action) => {
                state['messages'] = action['payload'];
            },
            setCurrentConversation: (state, action) => {
                state['currentConversationId'] = action['payload'];
            },
            clearConversation: (state) => {
                state['messages'] = [];
                state['currentConversationId'] = null;
                state['agentActions'] = [];
                state['pendingClassification'] = null;
            },
            setTyping: (state, action) => {
                state['isTyping'] = action['payload'];
            },
            addAgentAction: (state, action) => {
                state['agentActions'].push(action['payload']);
            },
            updateAgentAction: (state, action) => {
                const { index, status } = action['payload'];
                if (state['agentActions'][index]) state['agentActions'][index]['status'] = status;
            },
            clearAgentActions: (state) => {
                state['agentActions'] = [];
            },
            setPendingClassification: (state, action) => {
                state['pendingClassification'] = action['payload'];
            },
            clearPendingClassification: (state) => {
                state['pendingClassification'] = null;
            }
        },
        extraReducers: (builder) => {
            builder
                .addCase(sendClarenceMessage.pending, (state) => {
                    state['isLoading'] = true;
                    state['isTyping'] = true;
                    state['error'] = null;
                })
                .addCase(sendClarenceMessage.fulfilled, (state, action) => {
                    state['isLoading'] = false;
                    state['isTyping'] = false;
                    if (action['payload']['conversationId']) state['currentConversationId'] = action['payload']['conversationId'];
                    if (action['payload']['message']) state['messages'].push(action['payload']['message']);
                    if (action['payload']['pendingClassification']) state['pendingClassification'] = action['payload']['pendingClassification'];
                })
                .addCase(sendClarenceMessage.rejected, (state, action) => {
                    state['isLoading'] = false;
                    state['isTyping'] = false;
                    state['error'] = action['payload'];
                })
                .addCase(fetchClarenceConversations.pending, (state) => {
                    state['isLoading'] = true;
                })
                .addCase(fetchClarenceConversations.fulfilled, (state, action) => {
                    state['isLoading'] = false;
                    state['conversations'] = action['payload'];
                })
                .addCase(fetchClarenceConversations.rejected, (state, action) => {
                    state['isLoading'] = false;
                    state['error'] = action['payload'];
                })
                .addCase(fetchClarenceSuggestions.fulfilled, (state, action) => {
                    state['suggestions'] = action['payload'];
                })
                .addCase(confirmClarenceClassification.fulfilled, (state, action) => {
                    state['pendingClassification'] = null;
                    if (action['payload']['message']) state['messages'].push(action['payload']['message']);
                });
        }
    });

    return clarenceSlice.reducer;
}

export const toggleClarence = createAction("clarence/toggleClarence");
export const addClarenceMessage = createAction("clarence/addMessage");
export const setClarenceMessages = createAction("clarence/setMessages");
export const setCurrentConversation = createAction("clarence/setCurrentConversation");
export const clearClarenceConversation = createAction("clarence/clearConversation");
export const setClarenceTyping = createAction("clarence/setTyping");
export const addClarenceAgentAction = createAction("clarence/addAgentAction");
export const updateClarenceAgentAction = createAction("clarence/updateAgentAction");
export const clearClarenceAgentActions = createAction("clarence/clearAgentActions");
export const setPendingClassification = createAction("clarence/setPendingClassification");
export const clearPendingClassification = createAction("clarence/clearPendingClassification");
